import { getDateInputValidationMessage, validateDateInputString } from "@/common/helpers/date-input";
import { Button, ButtonVariant } from "@/components/ui/button";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Control, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { navigate } from "wouter/use-browser-location";
import { z } from "zod";

export const formSchema = z.object({
  newEarliestPickupDate: z
    .string()
    .trim()
    .min(1, "common:PickupFromIsRequired")
    .superRefine((value, context) => {
      const error = validateDateInputString(value, { requireFutureDate: true });

      if (error) {
        context.addIssue({ code: "custom", message: getDateInputValidationMessage(error) });
      }
    }),
});

export type formSchemaType = z.infer<typeof formSchema>;

interface NewDateFormFieldsProps {
  isLoading: boolean;
  formControl: Control<formSchemaType>;
  form: UseFormReturn<formSchemaType>;
  disabled: boolean;
  cancelNavigateTo: string;
}

export function NewDateFormFields({
  isLoading,
  formControl,
  form,
  disabled,
  cancelNavigateTo,
}: NewDateFormFieldsProps) {
  const { t } = useTranslation(["fit", "common"]);
  const { isSubmitted, touchedFields } = form.formState;
  const showNewEarliestPickupDateError = !!touchedFields.newEarliestPickupDate || isSubmitted;

  return (
    <>
      <FormField
        control={formControl}
        name="newEarliestPickupDate"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel showError={showNewEarliestPickupDateError}>{t("common:PickupFrom")}</FormLabel>
            <FormControl showError={showNewEarliestPickupDateError}>
              <DatePickerInput
                ref={field.ref}
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={() => {
                  field.onBlur();
                  void form.trigger("newEarliestPickupDate");
                }}
                pastDaysDisabled
              />
            </FormControl>
            <FormMessage showError={showNewEarliestPickupDateError} />
          </FormItem>
        )}
      />

      <div className="flex gap-2">
        <Button disabled={isLoading || disabled} type="submit">
          {t("common:Save")}
        </Button>
        <Button
          disabled={isLoading}
          onClick={() => navigate(cancelNavigateTo)}
          type="button"
          variant={ButtonVariant.Ghost}
        >
          {t("common:Cancel")}
        </Button>
      </div>
    </>
  );
}
